import { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";
import { type VariableValue } from "@doitintl/cmp-models/src/CloudFlowVariables";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";

import { cloudflowTexts } from "../../../assets/texts";
import { DeleteDialog } from "../../../Components/DeleteDialog";
import { DoitConsoleTitle } from "../../../Components/DoitConsoleTitle";
import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { FilterTableSkeleton } from "../../../Components/FilterTable/FilterTableSkeleton";
import { useCustomerContext } from "../../../Context/CustomerContext";
import CreateVariableModalWrapper from "../CloudflowBuilder/Common/CreateVariableModalWrapper";
import { VariableProvider } from "../CloudflowBuilder/Common/VariableProvider";
import GlobalVariableRow from "./GlobalVariableRow";
import { filters, headerColumns } from "./globalVariablesBrowserColumns";
import { useGlobalVariables } from "./VariableManager";

export const GlobalVariablesBrowser = () => {
  const { customer } = useCustomerContext();
  const history = useHistory();
  const { deleteVariable, globalVariables, loading } = useGlobalVariables();
  const [selectedIdToDelete, setSelectedIdToDelete] = useState<string>();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [initialData, setInitialData] = useState<VariableValue>();

  const returnToDashboard = () => {
    history.push(`/customers/${customer.id}/operate/cloudflow`);
  };

  const handleDeleteGlobalVariables = async () => {
    if (selectedIdToDelete) {
      try {
        await deleteVariable(selectedIdToDelete);
      } catch (e) {
        // catching the error here because DeleteDialog doesnt catch errors
        // not doing anything with the error because it is handled in the hook callback
        e;
      }
    }
  };

  const handleEdit = useCallback((item: VariableValue) => {
    setCreateModalOpen(true);
    setInitialData(item);
  }, []);

  const RowWrapper = useCallback(
    ({ row }: { row: VariableValue }) => (
      <GlobalVariableRow
        item={row}
        onDelete={(item) => {
          setSelectedIdToDelete(item.id);
        }}
        onEdit={handleEdit}
      />
    ),
    [handleEdit]
  );

  if (loading) {
    return (
      <Box
        sx={{
          p: 1,
        }}
      >
        <FilterTableSkeleton />
      </Box>
    );
  }

  return (
    <VariableProvider>
      <DoitConsoleTitle pageName={cloudflowTexts.GLOBAL_VARIABLES} />
      <FilterTable<VariableValue>
        tableItems={globalVariables}
        rowComponent={RowWrapper}
        headerColumns={headerColumns}
        filterColumns={filters}
        filterBarPlaceholder={cloudflowTexts.FILTER_TABLE}
        persistenceKey="global_variables_table"
        toolbarProps={{
          title: (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
              }}
            >
              <IconButton aria-label="Back" onClick={returnToDashboard} size="large">
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h1"
                sx={{
                  display: "inline",
                }}
              >
                {cloudflowTexts.GLOBAL_VARIABLES}
              </Typography>
            </Stack>
          ),
          customSlot: (
            <Stack direction="row" spacing={1}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  setCreateModalOpen(true);
                }}
              >
                {cloudflowTexts.CREATE_GLOBAL_VARIABLE}
              </Button>
              <Button variant="text" color="primary" startIcon={<HelpOutlineIcon />}>
                {cloudflowTexts.HOW_TO_USE_GLOBAL_VARIABLES}
              </Button>
            </Stack>
          ),
        }}
      />

      <DeleteDialog
        open={!!selectedIdToDelete}
        title={cloudflowTexts.DELETE_VARIABLE}
        message={cloudflowTexts.DELETE_VARIABLE_MESSAGE}
        onDelete={handleDeleteGlobalVariables}
        onClose={() => {
          setSelectedIdToDelete(undefined);
        }}
      />

      <CreateVariableModalWrapper
        createModalOpen={createModalOpen}
        setCreateModalOpen={setCreateModalOpen}
        initialData={initialData}
        setInitialData={setInitialData}
        scope="global"
      />
    </VariableProvider>
  );
};
